import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import API from '../../API/Main/API';
import {
	CreateSingleSessionCancellationPayload,
	CreateCancellationRequestPayload,
	PendingCancellationRequestViewModel,
	ScheduleCancellationViewModel,
	CancellationDecisionPayload,
} from '../../API/ClientAPIHelpers/scheduleCancellation';
import { ErrorProperties } from '../../API/identityAPIProperties';

interface ScheduleCancellationState {
	error: ErrorProperties;
	loading: boolean;
	providerCancellations: ScheduleCancellationViewModel[];
	pendingRequests: PendingCancellationRequestViewModel[];
	pendingRequestsLoading: boolean;
	submitting: boolean;
}

const initialState: ScheduleCancellationState = {
	error: { status: 0, title: '' },
	loading: false,
	providerCancellations: [],
	pendingRequests: [],
	pendingRequestsLoading: false,
	submitting: false,
};

export const createSingleSessionCancellation = createAsyncThunk(
	'scheduleCancellation/createSingle',
	async (data: CreateSingleSessionCancellationPayload, thunkAPI) => {
		const response = await API.ClientAPI.ScheduleCancellation.createSingleSession(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as ScheduleCancellationViewModel;
	},
);

export const createCancellationRequest = createAsyncThunk(
	'scheduleCancellation/createCancellationRequest',
	async (data: CreateCancellationRequestPayload, thunkAPI) => {
		const response = await API.ClientAPI.ScheduleCancellation.createCancellationRequest(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as ScheduleCancellationViewModel[];
	},
);

export const getPendingRequests = createAsyncThunk(
	'scheduleCancellation/getPending',
	async (branchId: string | undefined, thunkAPI) => {
		const response = await API.ClientAPI.ScheduleCancellation.getPendingRequests(branchId);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as PendingCancellationRequestViewModel[];
	},
);

export const approveCancellationGroup = createAsyncThunk(
	'scheduleCancellation/approve',
	async (groupId: string, thunkAPI) => {
		const response = await API.ClientAPI.ScheduleCancellation.approveGroup(groupId);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return groupId;
	},
);

export const denyCancellationGroup = createAsyncThunk(
	'scheduleCancellation/deny',
	async ({ groupId, data }: { data?: CancellationDecisionPayload; groupId: string }, thunkAPI) => {
		const response = await API.ClientAPI.ScheduleCancellation.denyGroup(groupId, data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return groupId;
	},
);

export const deleteCancellationGroup = createAsyncThunk(
	'scheduleCancellation/deleteGroup',
	async (groupId: string, thunkAPI) => {
		const response = await API.ClientAPI.ScheduleCancellation.deleteGroup(groupId);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return groupId;
	},
);

export const undoCancellation = createAsyncThunk(
	'scheduleCancellation/undoCancellation',
	async (id: string, thunkAPI) => {
		const response = await API.ClientAPI.ScheduleCancellation.undoCancellation(id);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return id;
	},
);

export const undoCancellationGroup = createAsyncThunk(
	'scheduleCancellation/undoGroup',
	async (groupId: string, thunkAPI) => {
		const response = await API.ClientAPI.ScheduleCancellation.undoGroup(groupId);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return groupId;
	},
);

export const getProviderCancellations = createAsyncThunk(
	'scheduleCancellation/getProviderCancellations',
	async ({ providerId, startDate, endDate }: { endDate?: string; providerId: string; startDate?: string }, thunkAPI) => {
		const response = await API.ClientAPI.ScheduleCancellation.getCancellations({ providerId, startDate, endDate });

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as ScheduleCancellationViewModel[];
	},
);

const scheduleCancellationSlice = createSlice({
	name: 'scheduleCancellation',
	initialState,
	reducers: {
		clearPendingRequests: (state) => {
			state.pendingRequests = [];
		},
		clearProviderCancellations: (state) => {
			state.providerCancellations = [];
		},
	},
	extraReducers: (builder) => {
		builder
			// Create single session cancellation
			.addCase(createSingleSessionCancellation.pending, (state) => {
				state.submitting = true;
			})
			.addCase(createSingleSessionCancellation.fulfilled, (state) => {
				state.submitting = false;
			})
			.addCase(createSingleSessionCancellation.rejected, (state) => {
				state.submitting = false;
			})
			// Create cancellation request
			.addCase(createCancellationRequest.pending, (state) => {
				state.submitting = true;
			})
			.addCase(createCancellationRequest.fulfilled, (state) => {
				state.submitting = false;
			})
			.addCase(createCancellationRequest.rejected, (state) => {
				state.submitting = false;
			})
			// Get pending requests
			.addCase(getPendingRequests.pending, (state) => {
				state.pendingRequestsLoading = true;
			})
			.addCase(getPendingRequests.fulfilled, (state, action) => {
				state.pendingRequestsLoading = false;
				state.pendingRequests = action.payload;
			})
			.addCase(getPendingRequests.rejected, (state) => {
				state.pendingRequestsLoading = false;
			})
			// Approve — remove from Redux list (UI card uses local state for status indicator)
			.addCase(approveCancellationGroup.fulfilled, (state, action) => {
				state.pendingRequests = state.pendingRequests.filter((r) => r.groupId !== action.payload);
			})
			// Deny — remove from Redux list (UI card uses local state for status indicator)
			.addCase(denyCancellationGroup.fulfilled, (state, action) => {
				state.pendingRequests = state.pendingRequests.filter((r) => r.groupId !== action.payload);
			})
			// Delete/withdraw — remove from Redux list
			.addCase(deleteCancellationGroup.fulfilled, (state, action) => {
				state.pendingRequests = state.pendingRequests.filter((r) => r.groupId !== action.payload);
			})
			// Provider cancellations across all statuses
			.addCase(getProviderCancellations.fulfilled, (state, action) => {
				state.providerCancellations = action.payload;
			});
	},
});

export const { clearPendingRequests, clearProviderCancellations } = scheduleCancellationSlice.actions;
export default scheduleCancellationSlice.reducer;
